import { useId, type ReactNode } from "react";
import { cn } from "@/shared/lib/utils";

type SwitchProps = {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  /** Rótulo visível; sem ele, passe `aria-label`. */
  label?: ReactNode;
  description?: ReactNode;
  disabled?: boolean;
  size?: "sm" | "md";
  id?: string;
  "aria-label"?: string;
  className?: string;
};

/** Interruptor liga/desliga (role="switch"). Para listas de opções use Checkbox. */
export function Switch({ checked, onCheckedChange, label, description, disabled, size = "md", id, className, ...aria }: SwitchProps) {
  const autoId = useId();
  const btnId = id ?? autoId;
  const track = size === "sm" ? "h-4 w-7" : "h-5 w-9";
  const thumb = size === "sm" ? "h-3 w-3" : "h-4 w-4";
  const shift = size === "sm" ? "translate-x-3" : "translate-x-4";

  const control = (
    <button
      id={btnId}
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label ? undefined : aria["aria-label"]}
      disabled={disabled}
      onClick={() => onCheckedChange(!checked)}
      className={cn(
        "relative inline-flex shrink-0 items-center rounded-full p-0.5 transition-colors duration-150 disabled:cursor-not-allowed disabled:opacity-50",
        track,
        checked ? "bg-primary" : "bg-border-strong",
      )}
    >
      <span className={cn("rounded-full bg-white shadow-xs transition-transform duration-150", thumb, checked && shift)} />
    </button>
  );

  if (!label) return <span className={className}>{control}</span>;
  return (
    <div className={cn("flex items-start justify-between gap-4", className)}>
      <label htmlFor={btnId} className={cn("min-w-0 cursor-pointer", disabled && "cursor-not-allowed opacity-60")}>
        <span className="block text-body font-medium text-fg">{label}</span>
        {description ? <span className="mt-0.5 block text-caption text-fg-subtle">{description}</span> : null}
      </label>
      {control}
    </div>
  );
}
